import React, { useState, useContext } from 'react';
import { ColumnDefinition } from '../reusable-components/GenericTable';
import { SearchResultsContainer } from '../reusable-components/SearchResultsContainer';
import { AuthContext } from '../reusable-components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DateRangePickerInput } from '../reusable-components/DateRangePickerInput';
import { TextInput } from '../reusable-components/TextInput';
import { SelectInput } from '../reusable-components/SelectInput';
import { LimitRecordsSelect } from '../reusable-components/LimitRecordsSelect';
import { SearchButtonGroup } from '../reusable-components/SearchButtonGroup';
import { JobSearchResult } from '../models/job/JobSearchResult';
import { normalizeDateRange } from '../utils/date-functions';
import { DateValueType } from 'react-tailwindcss-datepicker';
import { useJobSearch } from '../hooks/use-job-search';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import {
  checkTokenAndRenderCode,
  isTokenValid,
} from '../utils/common-functions';
import { showWarningMessage } from '../utils/toast-functions';
import { isBlank, isNumeric } from '../utils/common-functions';
import { MainComponentProps } from '../reusable-components/RouterComponent';
import { useShowMessageToast } from '../hooks/use-show-message-toast';
import { StandardToastContainer } from '../reusable-components/StandardToastContainer';
import { DownloadJobZipFileIcon } from '../icons/DownloadJobZipFileIcon';
import { DownloadJobLogsIcon } from '../icons/DownloadJobLogsIcon';
import { DeleteIcon } from '../icons/DeleteIcon';
import ExploreIcon from '../icons/ExploreIcon';
import { ITELLITY_BACKEND_BASE_URL } from '../services/constants';

export const RatingJobsPage: React.FC<MainComponentProps> = ({
  skipTokenCheck = false,
}) => {
  const [companyName, setCompanyName] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [jobState, setJobState] = useState<string>('');
  const authContext = useContext(AuthContext);
  const { token } = authContext;
  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();
  const [limitRecords, setLimitRecords] = useState<number>(10);
  useShowMessageToast();

  const currentDate = new Date();

  const tomorrowDate = () => {
    const today = currentDate;
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  };

  // date range picker
  const [dateRangeValue, setDateRangeValue] = useState<DateValueType>({
    startDate: currentDate,
    endDate: tomorrowDate(),
  });

  const { jobs, queryInProgress, searchJobs, handleDeleteJob } = useJobSearch(
    token,
    dateRangeValue,
    userName,
    userId,
    companyName,
    companyId,
    jobState,
    limitRecords,
  );

  const handleDownloadZipFile = async (_: string, row: JobSearchResult) => {
    if (row.jobState.toUpperCase() === 'DONE') {
      const url = `${ITELLITY_BACKEND_BASE_URL}/output/${row.userId}-${row.customerId}/${row.zipFileName}`;
      window.open(url, '_blank');
    } else {
      showWarningMessage(
        'Zip file is not available for downloading, because the job is not done yet.',
      );
    }
  };

  const handleBrowseLogFile = async (_: string, row: JobSearchResult) => {
    const url = `${ITELLITY_BACKEND_BASE_URL}/output/${row.userId}-${row.customerId}/job_${row.jobId}.log`;
    window.open(url, '_blank');
  };

  const handleExploreJob = async (_: string, row: JobSearchResult) => {
    const jobId: number = row.jobId;

    navigate(`/job/${jobId}`);
  };

  // define column titles and their ordering on the table
  const jobFieldDefs: Record<string, ColumnDefinition> = {
    jobId: { title: 'Job Id', index: 0 },
    customerName: { title: 'Customer', index: 1 },
    executionDate: { title: 'Execution Date', index: 2 },
    contractAmount: { title: 'Contract Amount', index: 3 },
    jobState: { title: 'Job State', index: 4 },
  };

  const checkSearchCondition = (): boolean => {
    return !queryInProgress && allFieldsValid() && isAnyFieldFilled();
  };

  const allFieldsValid = (): boolean => {
    return (
      (isNumeric(companyId) || isBlank(companyId)) &&
      (isNumeric(userId) || isBlank(userId))
    );
  };

  const isAnyFieldFilled = (): boolean => {
    return (
      !isBlank(companyName) ||
      isNumeric(companyId) ||
      !isBlank(userName) ||
      isNumeric(userId) ||
      !isBlank(jobState) ||
      dateRangeValue?.startDate !== null ||
      dateRangeValue?.endDate !== null
    );
  };

  const pageJSX: JSX.Element = (
    <div className="md:container md:mx-auto">
      <StandardToastContainer />

      <h1 className="text-2xl font-bold my-4">Rating Jobs</h1>

      <div className="flex flex-wrap gap-4">
        <TextInput
          label="Company Name:"
          value={companyName}
          placeholder="Any part of the company name here"
          custom_test_id="company-name-input"
          onChange={(e) => {
            setCompanyId('');
            setCompanyName(e.target.value);
            setUserId('');
            setUserName('');
          }}
          onEnterKeyPressed={() => checkSearchCondition() && searchJobs()}
          width="w-1/4"
        />
        <TextInput
          label="Company Id:"
          value={companyId}
          placeholder="Number here"
          custom_test_id="company-id-input"
          onChange={(e) => {
            setCompanyId(e.target.value);
            setCompanyName('');
            setUserId('');
            setUserName('');
          }}
          onEnterKeyPressed={() => checkSearchCondition() && searchJobs()}
          validation={isNumeric}
          errorMessage="Company Id must be a number"
          width="w-1/8"
        />

        <div className="relative z-20">
          <DateRangePickerInput
            label="Date Range:"
            value={dateRangeValue}
            onChange={(newValue: DateValueType) => {
              const newDateRange = normalizeDateRange(
                newValue?.startDate ?? null,
                newValue?.endDate ?? null,
              );
              setDateRangeValue(newDateRange);
            }}
          />
        </div>

        <LimitRecordsSelect
          value={limitRecords}
          onChange={setLimitRecords}
          customTestId="limit-records-input"
        />
      </div>
      <div className="md:container md:mx-auto">
        <div className="flex space-x-4">
          <TextInput
            label="Username:"
            value={userName}
            placeholder="The user name that started the job"
            onChange={(e) => {
              setCompanyId('');
              setCompanyName('');
              setUserId('');
              setUserName(e.target.value);
            }}
            onEnterKeyPressed={() => checkSearchCondition() && searchJobs()}
            custom_test_id="username-input"
          />
          <TextInput
            label="User Id:"
            value={userId}
            placeholder="Number here"
            onChange={(e) => {
              setCompanyId('');
              setCompanyName('');
              setUserId(e.target.value);
              setUserName('');
            }}
            onEnterKeyPressed={() => checkSearchCondition() && searchJobs()}
            validation={isNumeric}
            errorMessage="User Id must be a number"
            width="w-1/8"
            custom_test_id="user-id-input"
          />

          <SelectInput
            label="Job State:"
            value={jobState}
            onChange={(e) => setJobState(e.target.value)}
            valuesAndOptions={[
              { value: '', option: 'Please select a state' },
              { value: 'DONE', option: 'Done' },
              { value: 'START', option: 'Started' },
              { value: 'PROG', option: 'In Progress' },
              { value: 'ERROR', option: 'Error' },
            ]}
            width="w-1/6"
            custom_test_id="job-state-input"
          />
          <SearchButtonGroup
            queryInProgress={queryInProgress}
            onSearch={searchJobs}
            isSearchDisabled={
              queryInProgress ||
              !isTokenValid(token) ||
              !allFieldsValid() ||
              !isAnyFieldFilled()
            }
            addNewLink="/add-job"
            addNewTestId="add-job-link"
            addNewLabel="Add New Job"
          />
        </div>
      </div>

      <SearchResultsContainer
        items={jobs}
        queryInProgress={queryInProgress}
        itemsPerPage={10}
        columnDefs={jobFieldDefs}
        listOfActions={[
          [<DeleteIcon key="delete-icon" />, handleDeleteJob],
          [
            <DownloadJobZipFileIcon key="download-zip-file-icon" />,
            handleDownloadZipFile,
          ],
          [
            <DownloadJobLogsIcon key="download-logs-icon" />,
            handleBrowseLogFile,
          ],
          [<ExploreIcon key="explore-icon" />, handleExploreJob],
        ]}
      />
    </div>
  );

  if (skipTokenCheck) {
    return pageJSX;
  } else {
    return checkTokenAndRenderCode(token, pageJSX, setCurrentRoute, navigate);
  }
};
